import { PlusOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Drawer,
  Flex,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import Title from "antd/es/typography/Title";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "../../../../api";
import Loader from "../../../../components/Loader";
import { IntTplVehicleType } from "../../../../domain/types";
import { useAppSelector } from "../../../../store";
import { AppState } from "../../../../store/appSlice";
import AdminLangSwicher from "../../../admin_components/AdminLangSwicher";
import React from "react";
const Text = Typography;

const InternationalTplVehicleTypes = () => {
  const { t } = useTranslation();
  const { field_Language, lang } = useAppSelector(
    (data: { app: AppState }) => data.app
  );
  const [drawerStatus, setDrawerStatus] = useState<boolean>(false);
  const [editedState, setEditedState] =
    useState<Partial<IntTplVehicleType> | null>(null);

  const { data: IntTplPurposeList, isLoading: isIntTplPurposeLoading } =
    api.useGetIntTplPurposeQuery(undefined);

  const {
    data: IntTplVehicleTypeList,
    refetch: refetchIntTplVehicleType,
    isLoading: isIntTplVehicleTypeLoading,
  } = api.useGetIntTplVehicleTypeQuery(undefined);

  const {
    data: intTplVehicleTypeById,
    isLoading: isIntTplVehicleByIdLoading,
    isSuccess: intTplVehicleTypeSuccess,
    refetch: refetchTplVehicleById,
  } = api.useGetIntTplVehicleTypeByIdQuery(editedState?.id as number, {
    skip: !editedState?.id,
  });

  const _refetchVehicleType = useCallback(() => {
    if (editedState?.id) {
      refetchTplVehicleById();
    }
  }, [editedState?.id, refetchTplVehicleById]);

  useEffect(() => {
    _refetchVehicleType();

    return () => {};
  }, [_refetchVehicleType]);

  const [createVehicleType, { isLoading: isCreateIntTplVehicleTypeLoading }] =
    api.useCreateIntTplVehicleTypeMutation();

  const [updateVehicleType, { isLoading: isUpdateIntTplVehicleTypeLoading }] =
    api.useUpdateIntTplVehicleTypeMutation();

  const [
    deleteVehicleType,
    {
      isLoading: isDeleteIntTplVehicleTypeLoading,
      isSuccess: deleteIntTplVehicleTypeSuccess,
    },
  ] = api.useDeleteIntTplPurposeMutation();

  const onCloseDrawer = () => {
    setDrawerStatus(false);
    setEditedState(null);
  };

  const setEditedField = (field: keyof IntTplVehicleType, value: any) => {
    setEditedState({
      ...editedState,
      [field]: value,
    });
  };

  const buildPayload = (data: Partial<IntTplVehicleType>) => ({
    ...data,
    ligaAPIId: data.ligaApiId,
  });

  const _createVehicleType = async () => {
    if (editedState) {
      try {
        await createVehicleType(
          buildPayload(editedState) as Partial<IntTplVehicleType>
        ).unwrap();
        refetchIntTplVehicleType();
        toast.success(`${t("carType")} ${t("addedSuccessfully")}`);
        onCloseDrawer();
      } catch (error) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0];
          const errorMessage = firstError.message;
          toast.error(errorMessage);
        } else {
          toast.error(`${t("errorOccurred")}`);
        }
      }
    } else {
      toast.error(`${t("fillAllFields")}`);
    }
  };

  const _updateVehicleType = async () => {
    if (editedState) {
      try {
        await updateVehicleType(
          buildPayload(editedState) as Partial<IntTplVehicleType>
        ).unwrap();
        refetchIntTplVehicleType();
        toast.success(`${t("carType")} ${t("updatedSuccessfully")}`);
        onCloseDrawer();
      } catch (error) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0];
          const errorMessage = firstError.message;
          toast.error(errorMessage);
        } else {
          toast.error(`${t("errorOccurred")}`);
        }
      }
    } else {
      toast.error(`${t("fillAllFields")}`);
    }
  };

  useEffect(() => {
    if (deleteIntTplVehicleTypeSuccess) {
      refetchIntTplVehicleType();
    }
  }, [deleteIntTplVehicleTypeSuccess, refetchIntTplVehicleType]);

  useEffect(() => {
    if (
      intTplVehicleTypeById &&
      intTplVehicleTypeSuccess &&
      intTplVehicleTypeById.id === editedState?.id
    ) {
      setEditedState(intTplVehicleTypeById);
      setDrawerStatus(true);
    }

    return () => {};
  }, [intTplVehicleTypeById, intTplVehicleTypeSuccess, editedState?.id]);

  const MainLoader = isIntTplVehicleTypeLoading || isIntTplPurposeLoading;

  const DrawerLoader =
    isUpdateIntTplVehicleTypeLoading ||
    isDeleteIntTplVehicleTypeLoading ||
    isIntTplVehicleByIdLoading ||
    isCreateIntTplVehicleTypeLoading;

  return (
    <>
      {MainLoader ? (
        <Loader />
      ) : (
        <>
          <Flex justify="space-between">
            <Button
              size="large"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => [setEditedField("id", 0), setDrawerStatus(true)]}
            >
              {t("create")}
            </Button>

            <AdminLangSwicher />
          </Flex>

          <Divider />

          <Flex gap={20} wrap="wrap" justify="space-evenly">
            {IntTplVehicleTypeList?.map((item, index) => (
              <Badge.Ribbon
                key={index}
                style={{
                  marginTop: "-20px",
                }}
                text={item.status === "active" ? t("active") : t("inActive")}
                color={item.status === "active" ? "green" : "red"}
              >
                <Card
                  hoverable
                  style={{
                    maxWidth: "320px",
                    width: "100vw",
                    display: "grid",
                    placeItems: "center",
                    height: "120px",
                  }}
                  onClick={() => [
                    setEditedField("id", item.id),
                    setDrawerStatus(!drawerStatus),
                  ]}
                >
                  <Title level={5}>
                    {item.translations?.[field_Language]?.name ||
                      t("textNotFound")}
                  </Title>
                </Card>
              </Badge.Ribbon>
            ))}
          </Flex>

          <Drawer
            title={editedState?.id === 0 ? t("create") : t("update")}
            onClose={onCloseDrawer}
            open={drawerStatus}
            extra={<AdminLangSwicher />}
          >
            {DrawerLoader ? (
              <Loader />
            ) : (
              <Flex vertical gap={20}>
                <Space direction="vertical">
                  <Text>{t("carType")}</Text>
                  <Input
                    size="large"
                    placeholder={t("carType")}
                    value={
                      editedState?.translations?.[field_Language]?.name || ""
                    }
                    onChange={(event) =>
                      setEditedField("translations", {
                        ...editedState?.translations,
                        [field_Language]: {
                          ...editedState?.translations?.[field_Language],
                          name: event?.target?.value,
                        },
                      })
                    }
                  />
                </Space>

                <Checkbox.Group
                  value={editedState?.usageTypeIds || []}
                  onChange={(checkedValues) =>
                    setEditedField("usageTypeIds", checkedValues)
                  }
                >
                  <Row>
                    {IntTplPurposeList?.filter(
                      (x) => x.status === "active"
                    )?.map((item, index) => (
                      <Col span={12} key={index} style={{ marginBlock: 5 }}>
                        <Checkbox value={item.id}>
                          {item?.translations?.[lang]?.name}
                        </Checkbox>
                      </Col>
                    ))}
                  </Row>
                </Checkbox.Group>

                <Space direction="vertical">
                  <Text>{t("status")}</Text>
                  <Select
                    size="large"
                    showSearch
                    style={{ width: "100%", display: "block" }}
                    placeholder={t("status")}
                    optionFilterProp="label"
                    allowClear
                    value={editedState?.status || undefined}
                    onChange={(value) => setEditedField("status", value)}
                    options={[
                      {
                        status: "active",
                        name: t("active"),
                      },
                      {
                        status: "inactive",
                        name: t("inActive"),
                      },
                    ]?.map((item) => ({
                      value: item.status,
                      label: item.name,
                    }))}
                  />
                </Space>

                <Space direction="vertical">
                  <Text>{t("Liga API ID")}</Text>
                  <Input
                    size="large"
                    placeholder={t("ligaApiId")}
                    value={editedState?.ligaApiId ?? ""}
                    onChange={(event) => {
                      const rawValue = event?.target?.value ?? "";
                      setEditedField(
                        "ligaApiId",
                        rawValue === "" ? undefined : Number(rawValue)
                      );
                    }}
                  />
                </Space>

                <>
                  <Divider style={{ margin: 0 }} />
                  {editedState?.id === 0 ? (
                    <Button
                      disabled={
                        editedState && editedState.status ? false : true
                      }
                      size="large"
                      type="primary"
                      onClick={_createVehicleType}
                    >
                      {t("create")}
                    </Button>
                  ) : null}

                  {editedState && editedState.id && editedState.id !== 0 ? (
                    <>
                      <Button
                        size="large"
                        type="primary"
                        onClick={_updateVehicleType}
                      >
                        {t("update")}
                      </Button>

                      <Popconfirm
                        title={t("certainToDeleteIt")}
                        onConfirm={() => [
                          deleteVehicleType(editedState?.id as number),
                          onCloseDrawer(),
                        ]}
                        onCancel={() => {}}
                        okText="Yes"
                        cancelText="No"
                        placement="bottom"
                      >
                        <Button
                          danger
                          size="large"
                          style={{
                            width: "100%",
                          }}
                        >
                          {t("delete")}
                        </Button>
                      </Popconfirm>
                    </>
                  ) : null}
                </>
              </Flex>
            )}
          </Drawer>
        </>
      )}
    </>
  );
};

export default InternationalTplVehicleTypes;
